import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { WelcomeConfetti } from '../../components/WelcomeConfetti';
import axios from 'axios';
import {
  Shield, Users, BarChart3, Building2, UserPlus, ArrowRight,
  TrendingUp, AlertTriangle, UserCog, Trash2, Loader2, ChevronRight,
  UserX, RefreshCw, AlertCircle, CheckCircle2, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DELEGATABLE_FUNCTIONS = [
  { id: 'care_coordination', label: 'Care Coordination', desc: 'Manage care plans, journals, medications' },
  { id: 'shift_management', label: 'Shift Management', desc: 'Create, assign and manage shifts' },
  { id: 'check_in_monitoring', label: 'Check-In Monitoring', desc: 'Monitor caregiver check-ins/outs' },
  { id: 'hr_management', label: 'HR Management', desc: 'Performance reviews, warnings, leave' },
  { id: 'payroll', label: 'Payroll Processing', desc: 'Generate and approve payslips' },
  { id: 'invitations', label: 'Staff Invitations', desc: 'Send and manage invitation links' },
  { id: 'messaging', label: 'Messaging & Broadcasts', desc: 'Team chat, broadcasts, notifications' },
  { id: 'document_management', label: 'Document Management', desc: 'Manage staff documents and compliance' },
  { id: 'scheduling', label: 'AI Scheduling', desc: 'Use AI-powered shift scheduling' },
  { id: 'analytics', label: 'Analytics & Reports', desc: 'View dashboards and export reports' },
];

export function CEODashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [stats, setStats] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Delegation state
  const [orgUsers, setOrgUsers] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [delegating, setDelegating] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Transfer principal state
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferConfirm, setTransferConfirm] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Suspend/Remove state
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [actionUser, setActionUser] = useState(null);
  const [actionType, setActionType] = useState(''); // 'suspend' | 'remove'

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('ceo_welcome_seen');
    if (!hasSeenWelcome && user?.role === 'ceo') {
      setShowWelcome(true);
      localStorage.setItem('ceo_welcome_seen', 'true');
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, orgRes] = await Promise.all([
        axios.get(`${API_URL}/api/org/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/org/me`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setOrganization(orgRes.data);
    } catch {}
  };

  const fetchDelegationData = async () => {
    setLoadingUsers(true);
    try {
      const [usersRes, delegRes] = await Promise.all([
        axios.get(`${API_URL}/api/org/users-list`, { withCredentials: true }),
        axios.get(`${API_URL}/api/org/delegations`, { withCredentials: true })
      ]);
      setOrgUsers(usersRes.data);
      setDelegations(delegRes.data);
    } catch (err) {
      toast.error('Failed to load delegation data');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'delegation') fetchDelegationData();
  }, [activeTab]);

  const handleDelegate = async () => {
    if (!selectedUser || selectedFunctions.length === 0) {
      toast.error('Select a user and at least one function');
      return;
    }
    setDelegating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/org/delegate-admin`,
        { user_id: selectedUser, functions: selectedFunctions },
        { withCredentials: true }
      );
      toast.success(data.message);
      setSelectedUser('');
      setSelectedFunctions([]);
      fetchDelegationData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delegate');
    } finally {
      setDelegating(false);
    }
  };

  const handleRevoke = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/org/delegations/${userId}`, { withCredentials: true });
      toast.success('Delegation revoked');
      fetchDelegationData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const toggleFunction = (funcId) => {
    setSelectedFunctions(prev =>
      prev.includes(funcId) ? prev.filter(f => f !== funcId) : [...prev, funcId]
    );
  };

  // Transfer principal role
  const handleTransfer = async () => {
    if (!transferTarget || transferConfirm !== 'TRANSFER') return;
    setTransferring(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/org/transfer-principal`,
        { user_id: transferTarget, confirmation: 'TRANSFER' },
        { withCredentials: true }
      );
      toast.success(data.message);
      setShowTransfer(false);
      setTransferTarget('');
      setTransferConfirm('');
      // Redirect to login since role changed
      setTimeout(() => window.location.href = '/login', 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally { setTransferring(false); }
  };

  // Fetch members for suspend/remove
  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/org/members`, { withCredentials: true });
      setMembers(data);
    } catch { toast.error('Failed to load members'); }
    finally { setLoadingMembers(false); }
  };

  // Suspend user
  const handleSuspend = async (memberId) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/org/members/${memberId}/suspend`,
        { reason: suspendReason }, { withCredentials: true });
      toast.success(data.message);
      setSuspendReason('');
      setActionUser(null);
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to suspend'); }
  };

  // Reinstate user
  const handleReinstate = async (memberId) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/org/members/${memberId}/reinstate`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reinstate'); }
  };

  // Remove user
  const handleRemove = async (memberId) => {
    if (!window.confirm('Are you sure? This will permanently remove this user from the organization.')) return;
    try {
      const { data } = await axios.delete(`${API_URL}/api/org/members/${memberId}`, { withCredentials: true });
      toast.success(data.message);
      setActionUser(null);
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'delegation', label: 'HR Admin Delegation' },
  ];

  return (
    <DashboardLayout>
      <WelcomeConfetti show={showWelcome} onComplete={() => setShowWelcome(false)} />

      <div className="space-y-6" data-testid="ceo-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-6 h-6 text-[#FF6F61]" />
              <span className="text-sm text-[#FF6F61] font-medium">Principal Admin</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Welcome, {user?.name}
            </h1>
            <p className="text-muted-foreground mt-1">
              Full control over {organization?.name || 'your organization'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border/50 pb-2">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={`${activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}`}
              onClick={() => setActiveTab(t.id)} data-testid={`ceo-tab-${t.id}`}>
              {t.label}
            </Button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Caregivers', value: stats?.total_caregivers || 0, color: '#FF6F61', icon: Users },
                { label: 'Total Clients', value: stats?.total_clients || 0, color: '#A8E6CF', icon: Users },
                { label: 'Total Shifts', value: stats?.total_shifts || 0, color: '#00A6A0', icon: BarChart3 },
                { label: 'Pending Shifts', value: stats?.pending_shifts || 0, color: '#4A90E2', icon: AlertTriangle },
              ].map((s, i) => (
                <Card key={i} className="glass-card">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-muted-foreground text-xs">{s.label}</p>
                        <p className="text-2xl font-heading font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
                      </div>
                      <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${s.color}15` }}>
                        <s.icon className="w-5 h-5" style={{ color: s.color }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CEO Powers + Organization */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-base text-foreground flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#FF6F61]" />Principal Admin Powers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" className="w-full justify-between"
                    onClick={() => setActiveTab('delegation')} data-testid="manage-hr-admins-btn">
                    <span className="flex items-center gap-2"><UserCog className="w-4 h-4" />Manage HR Admins</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between"
                    onClick={() => { setShowTransfer(true); if (orgUsers.length === 0) fetchDelegationData(); }}
                    data-testid="delegate-power-btn">
                    <span className="flex items-center gap-2"><Shield className="w-4 h-4" />Delegate Principal Role</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between"
                    onClick={() => { setShowUserMgmt(true); fetchMembers(); }}
                    data-testid="suspend-user-btn">
                    <span className="flex items-center gap-2"><UserX className="w-4 h-4" />Suspend/Remove Users</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-base text-foreground flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#A8E6CF]" />Organization
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 rounded-lg bg-background/50">
                    <p className="text-xs text-muted-foreground">Organization Name</p>
                    <p className="text-foreground font-medium">{organization?.name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="text-xs text-muted-foreground">CISC</p>
                      <p className="text-[#FF6F61] font-mono text-sm">{organization?.cisc}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-background/50">
                      <p className="text-xs text-muted-foreground">Status</p>
                      <p className="text-[#A8E6CF] capitalize">{organization?.subscription_status}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Access Admin Dashboard - prominent CTA */}
            <Card className="glass-card border-[#00A6A0]/20">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-foreground">
                      Access Agency Admin Dashboard
                    </h3>
                    <p className="text-muted-foreground text-sm mt-1">
                      As Principal Admin, you have full access to all operational functions
                    </p>
                  </div>
                  <Button className="bg-[#00A6A0] hover:bg-[#008A85] text-white"
                    onClick={() => navigate('/dashboard/admin', { state: { fromCeo: true } })}
                    data-testid="access-admin-dashboard-btn">
                    Open Admin Dashboard
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Details */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base text-foreground">Your Details</CardTitle>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div><p className="text-xs text-muted-foreground">USIC</p><p className="text-[#FF6F61] font-mono">{user?.usic}</p></div>
                <div><p className="text-xs text-muted-foreground">Email</p><p className="text-foreground">{user?.email}</p></div>
                <div><p className="text-xs text-muted-foreground">Role</p><p className="text-foreground">Principal Admin (CEO)</p></div>
                <div><p className="text-xs text-muted-foreground">Company CISC</p><p className="text-foreground font-mono">{user?.cisc}</p></div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* HR ADMIN DELEGATION TAB */}
        {activeTab === 'delegation' && (
          <div className="space-y-6" data-testid="delegation-tab">
            {/* Delegate New Admin */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#00A6A0]" />Delegate Admin Functions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1.5">Select User from Network</label>
                  {loadingUsers ? (
                    <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading users...</div>
                  ) : (
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className="w-full p-2.5 rounded-lg bg-background border border-border text-foreground text-sm"
                      data-testid="delegation-user-select"
                    >
                      <option value="">-- Select a user --</option>
                      {orgUsers.filter(u => !delegations.find(d => d.user_id === u.id)).map(u => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email}) - {u.role} {u.staff_number ? `[${u.staff_number}]` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="text-xs text-muted-foreground block mb-2">Select Admin Functions to Delegate</label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {DELEGATABLE_FUNCTIONS.map(func => (
                      <button
                        key={func.id}
                        onClick={() => toggleFunction(func.id)}
                        className={`p-3 rounded-lg text-left transition-all border ${
                          selectedFunctions.includes(func.id)
                            ? 'bg-[#00A6A0]/10 border-[#00A6A0]/30 text-[#00A6A0]'
                            : 'bg-background/50 border-border text-muted-foreground hover:border-border/80'
                        }`}
                        data-testid={`func-${func.id}`}
                      >
                        <p className="text-xs font-medium">{func.label}</p>
                        <p className="text-[10px] opacity-70 mt-0.5">{func.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  className="bg-[#00A6A0] hover:bg-[#008A85] text-white"
                  onClick={handleDelegate}
                  disabled={!selectedUser || selectedFunctions.length === 0 || delegating}
                  data-testid="delegate-submit-btn"
                >
                  {delegating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCog className="w-4 h-4 mr-2" />}
                  Delegate Selected Functions
                </Button>
              </CardContent>
            </Card>

            {/* Current Delegations */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#FF6F61]" />Current HR Admin Delegations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {delegations.length === 0 ? (
                  <p className="text-muted-foreground text-center py-6 text-sm">No admin delegations yet. Select a user above to delegate functions.</p>
                ) : (
                  <div className="space-y-3">
                    {delegations.map(d => (
                      <div key={d.user_id} className="flex items-start justify-between p-4 rounded-lg bg-background/50 border border-border"
                        data-testid={`delegation-${d.user_id}`}>
                        <div>
                          <p className="text-sm font-medium text-foreground">{d.user_name}</p>
                          <p className="text-[10px] text-muted-foreground">{d.user_email}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {(d.functions || []).map(f => {
                              const func = DELEGATABLE_FUNCTIONS.find(df => df.id === f);
                              return (
                                <span key={f} className="text-[9px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">
                                  {func?.label || f}
                                </span>
                              );
                            })}
                          </div>
                          <p className="text-[9px] text-muted-foreground mt-1">
                            Delegated {d.delegated_at ? new Date(d.delegated_at).toLocaleDateString() : ''}
                            {d.original_role ? ` (was: ${d.original_role})` : ''}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                          onClick={() => handleRevoke(d.user_id)} data-testid={`revoke-${d.user_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Transfer Principal Role Dialog */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="transfer-principal-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Transfer Principal Role
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
              <p className="text-sm text-amber-300 font-medium">Warning: This action is irreversible</p>
              <p className="text-xs text-muted-foreground mt-1">
                The selected user will become the CEO. You will become an Agency Admin. You must log in again after this change.
              </p>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1.5">Select New Principal</label>
              <select
                className="w-full rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm"
                value={transferTarget}
                onChange={(e) => setTransferTarget(e.target.value)}
                data-testid="transfer-target-select"
              >
                <option value="">Choose a user...</option>
                {orgUsers.filter(u => u.role !== 'ceo').map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role}) — {u.email}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-muted-foreground block mb-1.5">
                Type <strong className="text-foreground">TRANSFER</strong> to confirm
              </label>
              <input
                className="w-full rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm"
                value={transferConfirm}
                onChange={(e) => setTransferConfirm(e.target.value)}
                placeholder="TRANSFER"
                data-testid="transfer-confirm-input"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowTransfer(false)} className="border-border">Cancel</Button>
              <Button
                onClick={handleTransfer}
                disabled={!transferTarget || transferConfirm !== 'TRANSFER' || transferring}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="transfer-confirm-btn"
              >
                {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Transfer Role'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspend/Remove Users Dialog */}
      <Dialog open={showUserMgmt} onOpenChange={setShowUserMgmt}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="user-mgmt-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <UserX className="w-5 h-5 text-[#FF6F61]" /> Manage Users
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {loadingMembers ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No members found</p>
            ) : (
              members.filter(m => m.role !== 'ceo').map((m) => (
                <div key={m.id} className="p-3 rounded-lg border border-border hover:border-[#384752] transition-all" data-testid={`member-${m.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{m.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{m.role}</span>
                        {m.status === 'suspended' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-400/20 text-red-400">Suspended</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                      {m.suspended_at && <p className="text-[10px] text-red-400 mt-0.5">Suspended: {new Date(m.suspended_at).toLocaleDateString()}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {m.status === 'suspended' ? (
                        <Button size="sm" variant="outline" className="text-xs text-[#A8E6CF] border-[#A8E6CF]/30 h-7 gap-1"
                          onClick={() => handleReinstate(m.id)} data-testid={`reinstate-${m.id}`}>
                          <RefreshCw className="w-3 h-3" /> Reinstate
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="text-xs text-amber-400 border-amber-400/30 h-7 gap-1"
                          onClick={() => { setActionUser(m); setActionType('suspend'); }}
                          data-testid={`suspend-${m.id}`}>
                          <AlertCircle className="w-3 h-3" /> Suspend
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="text-xs text-red-400 border-red-400/30 h-7 gap-1"
                        onClick={() => handleRemove(m.id)} data-testid={`remove-${m.id}`}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </Button>
                    </div>
                  </div>

                  {/* Suspend reason input */}
                  {actionUser?.id === m.id && actionType === 'suspend' && (
                    <div className="mt-3 flex gap-2">
                      <input
                        className="flex-1 rounded-md border border-border bg-muted text-foreground px-3 py-1.5 text-xs"
                        placeholder="Reason for suspension (optional)"
                        value={suspendReason}
                        onChange={(e) => setSuspendReason(e.target.value)}
                        data-testid={`suspend-reason-${m.id}`}
                      />
                      <Button size="sm" className="bg-amber-500 text-white text-xs h-7"
                        onClick={() => handleSuspend(m.id)} data-testid={`confirm-suspend-${m.id}`}>
                        Confirm
                      </Button>
                      <Button size="sm" variant="ghost" className="text-xs h-7"
                        onClick={() => { setActionUser(null); setSuspendReason(''); }}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default CEODashboard;
